import React, { ChangeEventHandler, useCallback, useEffect, useState } from 'react';
import { Input, Text } from '@ydistri/ds';
import TemplateMultiselectAndSort from './TemplateMultiselectAndSort';
import { TemplateResponse } from '@ydistri/api-sdk';

export type MergedCalculationTitles = Record<number, string | undefined>;

export interface MergedCalculationData {
  sortedTemplateIds: number[];
  calculationTitles: MergedCalculationTitles;
  /** What each template's validator said; a template it has not answered for yet is not valid. */
  templateValidity: Record<number, boolean | undefined>;
}

interface TemplateMultiselectAndSortProps {
  onChange: (v: MergedCalculationData) => void;
  defaultValues?: MergedCalculationData;
  /** What the server refused on a row of the last request, shown under that row's title. */
  serverErrors?: Record<number, string[]>;
}

const MergedCalculationTemplateSelect: React.FC<TemplateMultiselectAndSortProps> = ({
  onChange,
  defaultValues,
  serverErrors,
}) => {
  const [sortedTemplateIds, setSortedTemplateIds] = useState<number[]>(() => {
    if (defaultValues) {
      return defaultValues.sortedTemplateIds;
    } else {
      return [];
    }
  });
  const [calculationTitles, setCalculationTitles] = useState<MergedCalculationTitles>(() => {
    if (defaultValues) {
      return defaultValues.calculationTitles;
    } else {
      return {};
    }
  });

  const onTemplateChangeHandler = useCallback(
    (x: number[]) => {
      setSortedTemplateIds(x);
    },
    [setSortedTemplateIds],
  );

  const [templateValidity, setTemplateValidity] = useState<Record<number, boolean | undefined>>({});

  const onRemoveTemplate = useCallback((templateId: number) => {
    setCalculationTitles(p => {
      return { ...p, [templateId]: undefined };
    });
    setTemplateValidity(p => ({ ...p, [templateId]: undefined }));
  }, []);

  const onTemplateValidChange = useCallback((templateId: number, valid: boolean) => {
    setTemplateValidity(p => (p[templateId] === valid ? p : { ...p, [templateId]: valid }));
  }, []);

  const onChangeCalculationTitle = useCallback((templateId: number, calculationTitle: string) => {
    setCalculationTitles(p => ({ ...p, [templateId]: calculationTitle }));
  }, []);

  const onChangeInput = useCallback(
    (templateId: number) => {
      const eventHandler: ChangeEventHandler<HTMLInputElement> = e => {
        onChangeCalculationTitle(templateId, e.target.value);
      };

      return eventHandler;
    },
    [onChangeCalculationTitle],
  );

  const customElement = useCallback(
    (templateId: number, index: number) => {
      const errors = serverErrors?.[templateId] ?? [];
      return (
        <>
          <Text>[{index + 1}]</Text>
          <Input
            size="large"
            value={calculationTitles[templateId]}
            infoFlexGrow={1}
            onChange={onChangeInput(templateId)}
            status={errors.length > 0 ? 'error' : undefined}
            suffix={
              <Text $type="light" $size="small">
                template #{templateId}
              </Text>
            }
            $addPadding
          />
          {errors.length > 0 && <Text $type="danger">{errors.join(' ')}</Text>}
        </>
      );
    },
    [calculationTitles, onChangeInput, serverErrors],
  );

  const onTemplateAddedPrefillTitle = useCallback(
    (tpl: TemplateResponse) => {
      onChangeCalculationTitle(tpl.id, tpl.title);
    },
    [onChangeCalculationTitle],
  );

  useEffect(() => {
    onChange({ sortedTemplateIds, calculationTitles, templateValidity });
  }, [calculationTitles, onChange, sortedTemplateIds, templateValidity]);

  return (
    <TemplateMultiselectAndSort
      defaultValues={defaultValues?.sortedTemplateIds}
      customElements={customElement}
      onChangeSelectedTemplates={onTemplateChangeHandler}
      onCustomTemplateEvent={onTemplateAddedPrefillTitle}
      onRemoveTemplate={onRemoveTemplate}
      onTemplateValidChange={onTemplateValidChange}
      validator
    />
  );
};

export default MergedCalculationTemplateSelect;
